import { useEffect, useState, useMemo, type CSSProperties, type FormEvent } from "react";
import {
  listKnowledgeArticles,
  saveKnowledgeArticle,
  deleteKnowledgeArticle,
  askTheSentinel,
  type KnowledgeArticle,
  type AskSentinelResult,
} from "../../lib/api";
import OperationsStudioTabs from "../../components/admin/OperationsStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

const CATEGORIES = [
  { id: "all", label: "All SOPs" },
  { id: "bar_operations", label: "Bar Operations" },
  { id: "opening_closing", label: "Opening / Closing" },
  { id: "guest_experience", label: "Guest Experience" },
  { id: "inventory", label: "Inventory" },
  { id: "emergency", label: "Emergency" },
  { id: "memberships", label: "Memberships" },
  { id: "general", label: "General" },
];

export default function AdminKnowledge() {
  // Phones only (L121): a long link in an SOP body ran the card preview off the screen.
  const isPhone = useIsMobile(768);
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Ask The Sentinel State
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [aiAnswer, setAiAnswer] = useState<AskSentinelResult | null>(null);

  // Viewer Modal
  const [viewingArticle, setViewingArticle] = useState<KnowledgeArticle | null>(null);

  // Editor Modal
  const [editingArticle, setEditingArticle] = useState<Partial<KnowledgeArticle> | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (viewingArticle) setViewingArticle(null);
        if (isEditorOpen) setIsEditorOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [viewingArticle, isEditorOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await listKnowledgeArticles();
      setArticles(data || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load knowledge base", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredArticles = useMemo(() => {
    let list = articles;
    if (selectedCategory !== "all") {
      list = list.filter((a) => (a.category || "").toLowerCase() === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.body.toLowerCase().includes(q) ||
          a.slug.toLowerCase().includes(q)
      );
    }
    return list;
  }, [articles, selectedCategory, searchQuery]);

  const handleAsk = async (e: FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;
    setAsking(true);
    try {
      const res = await askTheSentinel(question.trim());
      setAiAnswer(res);
    } catch (err: any) {
      showToast(err.message || "Failed to query AI assistant", "err");
    } finally {
      setAsking(false);
    }
  };

  const handleNewArticle = () => {
    setEditingArticle({
      title: "",
      slug: "",
      category: "bar_operations",
      body: "## Standard Operating Procedure\n\n- Step 1: ...\n- Step 2: ...",
      video_url: "",
      is_published: true,
    });
    setIsEditorOpen(true);
  };

  const handleEditArticle = (a: KnowledgeArticle) => {
    setEditingArticle({ ...a });
    setViewingArticle(null);
    setIsEditorOpen(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingArticle || !editingArticle.title) return;
    setSaving(true);
    try {
      const payload: Partial<KnowledgeArticle> = {
        ...editingArticle,
        slug: editingArticle.slug || slugify(editingArticle.title),
      };
      await saveKnowledgeArticle(payload);
      showToast(`✓ SOP "${payload.title}" saved!`);
      setIsEditorOpen(false);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to save article", "err");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete SOP "${title}"?`)) return;
    try {
      await deleteKnowledgeArticle(id);
      showToast(`✓ Deleted "${title}"`);
      setIsEditorOpen(false);
      setViewingArticle(null);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to delete article", "err");
    }
  };

  return (
    <div style={styles.container}>
      <OperationsStudioTabs activeTab="knowledge" badgeCounts={{ knowledge: articles.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Knowledge Base &amp; Staff SOPs</h1>
          <p style={styles.subtitle}>
            Ask The Sentinel operational questions, browse standard operating procedures, and update staff guides.
          </p>
        </div>
        <button type="button" style={styles.btnPrimary} onClick={handleNewArticle}>
          + New SOP Article
        </button>
      </div>

      {/* "Ask The Sentinel" AI Assistant */}
      <div style={styles.askSection}>
        <div style={styles.askLabel}>Ask The Sentinel — AI Staff Assistant</div>
        <form onSubmit={handleAsk} style={styles.askForm}>
          <textarea
            rows={2}
            placeholder="Ask anything: 'What is the spill procedure behind the bar?', 'How do I comp a drink for VIP?', 'Opening checklist steps'..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAsk(e);
              }
            }}
            style={styles.askInput}
          />
          <button type="submit" style={styles.askBtn} disabled={asking}>
            {asking ? "Consulting…" : "Ask Assistant"}
          </button>
        </form>

        {aiAnswer && (
          <div style={styles.answerArea}>
            <div style={styles.answerText}>{aiAnswer.answer}</div>
            {aiAnswer.sources && aiAnswer.sources.length > 0 && (
              <div style={styles.sourceRow}>
                <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700 }}>Cited Sources:</span>
                {aiAnswer.sources.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    style={styles.sourceChip}
                    onClick={() => {
                      const found = articles.find((a) => a.id === s.id);
                      if (found) setViewingArticle(found);
                    }}
                  >
                    📖 {s.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Category Filter Pills & Search */}
      <div style={styles.filterRow}>
        <div style={styles.categoryChips}>
          {CATEGORIES.map((cat) => {
            const count =
              cat.id === "all"
                ? articles.length
                : articles.filter((a) => (a.category || "general").toLowerCase() === cat.id).length;
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                style={{
                  ...styles.chip,
                  borderColor: isActive ? "var(--gold)" : "var(--border)",
                  background: isActive ? "rgba(249,175,54,0.15)" : "var(--surface)",
                  color: isActive ? "var(--gold)" : "var(--muted)",
                }}
                onClick={() => setSelectedCategory(cat.id)}
              >
                {cat.label} <span style={styles.chipCount}>{count}</span>
              </button>
            );
          })}
        </div>
        <input
          type="text"
          placeholder="Search SOPs by title, text, slug…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Articles Grid */}
      {loading ? (
        <div style={styles.loader}>Loading knowledge base…</div>
      ) : filteredArticles.length === 0 ? (
        <div style={styles.emptyState}>No SOP articles found matching criteria.</div>
      ) : (
        <div style={styles.articlesGrid}>
          {filteredArticles.map((a) => (
            <div
              key={a.id}
              style={isPhone ? { ...styles.articleCard, minWidth: 0 } : styles.articleCard}
              onClick={() => setViewingArticle(a)}
            >
              <div style={styles.cardHeader}>
                <span style={styles.catBadge}>{a.category.replace("_", " ")}</span>
                <span
                  style={{
                    ...styles.statusBadge,
                    background: a.is_published ? "rgba(39,174,96,0.15)" : "rgba(138,122,102,0.15)",
                    color: a.is_published ? "#27ae60" : "var(--muted)",
                  }}
                >
                  {a.is_published ? "PUBLISHED" : "DRAFT"}
                </span>
              </div>

              <h3 style={isPhone ? { ...styles.cardTitle, ...phoneWrap } : styles.cardTitle}>{a.title}</h3>
              <p style={isPhone ? { ...styles.cardSnippet, ...phoneWrap } : styles.cardSnippet}>{a.body.replace(/[#*`]/g, "").substring(0, 140)}…</p>

              <div style={styles.cardFooter}>
                <span>{a.video_url ? "▶ Has Video" : "Doc SOP"}</span>
                <span style={{ color: "var(--gold)", fontWeight: 700 }}>Read SOP →</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Viewer Modal */}
      {viewingArticle && (
        <div style={styles.modalOverlay} onClick={() => setViewingArticle(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <span style={styles.catBadge}>{viewingArticle.category.replace("_", " ")}</span>
                <h2 style={styles.modalTitle}>{viewingArticle.title}</h2>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  style={styles.btnSecondary}
                  onClick={() => handleEditArticle(viewingArticle)}
                >
                  Edit SOP
                </button>
                <button
                  type="button"
                  style={styles.btnIcon}
                  onClick={() => setViewingArticle(null)}
                >
                  &times;
                </button>
              </div>
            </div>

            <div style={styles.viewerBody}>
              {viewingArticle.video_url && (
                <div style={{ marginBottom: 16 }}>
                  <a
                    href={viewingArticle.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.videoBtn}
                  >
                    ▶ Watch Video Walkthrough ({viewingArticle.video_url.replace(/^https?:\/\//, "").substring(0, 30)}…)
                  </a>
                </div>
              )}
              <div style={styles.markdownContent}>{viewingArticle.body}</div>
            </div>
          </div>
        </div>
      )}

      {/* Editor Modal */}
      {isEditorOpen && editingArticle && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingArticle.id ? `Edit: ${editingArticle.title}` : "New SOP Article"}
              </h2>
              <button
                type="button"
                style={styles.btnIcon}
                onClick={() => setIsEditorOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSave} style={styles.modalForm}>
              <div className="station-form-grid" style={styles.formGrid}>
                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Article Title *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. VIP Spill & Comp Protocol"
                    value={editingArticle.title || ""}
                    onChange={(e) => {
                      const title = e.target.value;
                      setEditingArticle({
                        ...editingArticle,
                        title,
                        slug: editingArticle.slug || slugify(title),
                      });
                    }}
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Category</label>
                  <select
                    value={editingArticle.category || "bar_operations"}
                    onChange={(e) =>
                      setEditingArticle({ ...editingArticle, category: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="bar_operations">Bar Operations</option>
                    <option value="opening_closing">Opening / Closing</option>
                    <option value="guest_experience">Guest Experience</option>
                    <option value="inventory">Inventory</option>
                    <option value="emergency">Emergency</option>
                    <option value="memberships">Memberships</option>
                    <option value="general">General</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Video Walkthrough URL</label>
                  <input
                    type="url"
                    placeholder="YouTube or Loom video link"
                    value={editingArticle.video_url || ""}
                    onChange={(e) =>
                      setEditingArticle({ ...editingArticle, video_url: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Markdown Body *</label>
                  <textarea
                    rows={12}
                    required
                    placeholder="## Protocol Overview\n\n- Step 1...\n- Step 2..."
                    value={editingArticle.body || ""}
                    onChange={(e) =>
                      setEditingArticle({ ...editingArticle, body: e.target.value })
                    }
                    style={{ ...styles.textarea, fontFamily: "monospace", fontSize: 13 }}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text)", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={editingArticle.is_published || false}
                      onChange={(e) =>
                        setEditingArticle({ ...editingArticle, is_published: e.target.checked })
                      }
                    />
                    Published (Visible to all staff members)
                  </label>
                </div>
              </div>

              <div style={styles.modalActions}>
                {editingArticle.id && (
                  <button
                    type="button"
                    style={styles.btnDanger}
                    onClick={() =>
                      handleDelete(editingArticle.id!, editingArticle.title || "Article")
                    }
                  >
                    Delete Article
                  </button>
                )}
                <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
                  <button
                    type="button"
                    style={styles.btnSecondary}
                    onClick={() => setIsEditorOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" style={styles.btnPrimary} disabled={saving}>
                    {saving ? "Saving…" : "Save SOP Article"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// break-word splits only a word wider than the whole line (a URL or email), never ordinary words.
const phoneWrap: CSSProperties = { overflowWrap: "break-word" };

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  askSection: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, marginBottom: 24 },
  askLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--gold)", letterSpacing: 0.5, marginBottom: 8 },
  askForm: { display: "flex", gap: 10, alignItems: "flex-end" },
  askInput: { flex: 1, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "10px 12px", fontSize: 13, resize: "none", lineHeight: 1.4 },
  askBtn: { background: "var(--gold)", color: "#111", border: 0, padding: "10px 18px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" },
  answerArea: { marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" },
  answerText: { fontSize: 14, lineHeight: 1.6, color: "var(--text)", whiteSpace: "pre-wrap" },
  sourceRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10 },
  sourceChip: { background: "rgba(249,175,54,0.1)", border: "1px solid rgba(249,175,54,0.3)", color: "var(--gold)", padding: "4px 10px", borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: "pointer" },
  filterRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 },
  categoryChips: { display: "flex", gap: 6, flexWrap: "wrap" },
  chip: { border: "1px solid", borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  chipCount: { background: "rgba(0,0,0,0.2)", borderRadius: 10, padding: "1px 6px", fontSize: 10 },
  searchInput: { minWidth: 260, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  articlesGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 },
  articleCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, cursor: "pointer", display: "flex", flexDirection: "column", gap: 8 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  catBadge: { fontSize: 10, fontWeight: 800, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5 },
  statusBadge: { fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4 },
  cardTitle: { fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)" },
  cardSnippet: { fontSize: 12, color: "var(--muted)", margin: 0, lineHeight: 1.4 },
  cardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: "auto", fontSize: 11, color: "var(--muted)" },
  btnPrimary: { background: "var(--gold)", color: "#111", border: 0, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  btnSecondary: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnDanger: { background: "transparent", color: "#E05040", border: "1px solid rgba(224,80,64,0.4)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnIcon: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 22, cursor: "pointer" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modalContent: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  viewerBody: { padding: 20, overflowY: "auto" },
  videoBtn: { display: "inline-block", background: "rgba(249,175,54,0.15)", color: "var(--gold)", border: "1px solid rgba(249,175,54,0.4)", padding: "8px 14px", borderRadius: 6, textDecoration: "none", fontSize: 12, fontWeight: 700 },
  markdownContent: { fontSize: 14, lineHeight: 1.7, color: "var(--text)", whiteSpace: "pre-wrap" },
  modalForm: { padding: 20, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", letterSpacing: 0.5 },
  input: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  select: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  textarea: { width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "10px 12px", fontSize: 13, resize: "vertical" },
  modalActions: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 16 },
};
